import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AdminProvider } from "@/admin/AdminContext";
import ProtectedRoute from "@/admin/ProtectedRoute";
import GtmRouteTracker from "@/lib/GtmRouteTracker";
import PaidAds from "@/pages/PaidAds";
import Whatsapp from "@/pages/Whatsapp";
import SalesSystem from "@/pages/SalesSystem";
import EcommerceSolutions from "@/pages/EcommerceSolutions";

const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const Terms = lazy(() => import("@/pages/Terms"));
const AdminLogin = lazy(() => import("@/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("@/admin/AdminDashboard"));
const AdminLeadDetail = lazy(() => import("@/admin/AdminLeadDetail"));
const AdminPipeline = lazy(() => import("@/admin/AdminPipeline"));
const AdminCampaigns = lazy(() => import("@/admin/AdminCampaigns"));
const AdminCampaignDetail = lazy(() => import("@/admin/AdminCampaignDetail"));
const AdminTeam = lazy(() => import("@/admin/AdminTeam"));

// In-app fallback for the legacy URL (index.html redirects before load); keeps query + hash.
function LegacySalesRedirect() {
  const { search, hash } = useLocation();
  return <Navigate to={`/sales-system${search}${hash}`} replace />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AdminProvider>
          <GtmRouteTracker />
          <Suspense fallback={<div className="min-h-screen" data-testid="route-loading" />}>
          <Routes>
            <Route path="/" element={<Navigate to="/paid-ads-management" replace />} />
            <Route path="/paid-ads-management" element={<PaidAds />} />
            <Route path="/whatsapp-automation" element={<Whatsapp />} />
            <Route path="/sales-system" element={<SalesSystem />} />
            <Route path="/ecommerce-solutions" element={<EcommerceSolutions />} />
            <Route path="/website-app-creation" element={<LegacySalesRedirect />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/leads/:kind/:id"
              element={
                <ProtectedRoute>
                  <AdminLeadDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/team"
              element={
                <ProtectedRoute>
                  <AdminTeam />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/pipeline"
              element={
                <ProtectedRoute>
                  <AdminPipeline />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/campaigns"
              element={
                <ProtectedRoute>
                  <AdminCampaigns />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/campaigns/:id"
              element={
                <ProtectedRoute>
                  <AdminCampaignDetail />
                </ProtectedRoute>
              }
            />
            <Route path="/admin/*" element={<Navigate to="/admin" replace />} />
            <Route path="*" element={<Navigate to="/paid-ads-management" replace />} />
          </Routes>
          </Suspense>
        </AdminProvider>
      </BrowserRouter>
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: "#ffffff",
            color: "#18181b",
            border: "1px solid #e5e7eb",
            boxShadow: "0 10px 30px -10px rgba(0,0,0,0.15)",
          },
        }}
      />
    </div>
  );
}

export default App;
